import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@snipit/core/context/AuthContext';
import { useSnippets } from '@snipit/core/hooks/useSnippets';
import { useOrganization } from '@snipit/core/hooks/useOrganization';
import { useTeams } from '@snipit/core/hooks/useTeams';
import { 
  Code, 
  Terminal, 
  Globe, 
  Settings, 
  CreditCard, 
  Zap, 
  Users, 
  ArrowRight,
  LayoutDashboard,
  Building,
  Download,
  BookOpen
} from 'lucide-react';

export default function DashboardHomeView() {
  const t = (str) => str;
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 8000);
    return () => clearInterval(timer);
  }, []);
  const { userProfile, userLicense, currentUser } = useAuth();
  const { organization } = useOrganization();
  const { teams } = useTeams();
  const { snippets } = useSnippets(organization, teams);

  const snippetCount = snippets ? snippets.length : 0;
  
  // Categorize snippets
  const snippetStats = useMemo(() => {
    if (!snippets) return { dev: 0, ai: 0, links: 0, tools: 0 };
    return snippets.reduce((acc, snippet) => {
      if (snippet.vault === 'dev') acc.dev++;
      else if (snippet.vault === 'ai') acc.ai++;
      else if (snippet.vault === 'links') acc.links++;
      else if (snippet.vault === 'tools') acc.tools++;
      return acc;
    }, { dev: 0, ai: 0, links: 0, tools: 0 });
  }, [snippets]);

  const setVaultAndNavigate = (vaultName) => {
    chrome.storage.local.set({ currentVault: vaultName }, () => {
      window.dispatchEvent(new CustomEvent('vaultChanged', { detail: vaultName }));
      navigate('/vault');
    });
  };

  const getTierDisplay = (tier) => {
    switch(tier) {
      case 'premium': return { name: 'Premium', color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' };
      case 'enterprise': return { name: 'Enterprise', color: 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30' };
      case 'enterprise_org': return { name: 'Organization', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
      default: return { name: 'Free Tier', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
    }
  };

  const tierInfo = getTierDisplay(userLicense?.tier);

  return (
    <div className="w-full h-full bg-bg-main p-8 overflow-y-auto">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
            Welcome back, <span className="bg-clip-text text-transparent bg-linear-to-r from-blue-400 to-cyan-400">{userProfile?.displayName?.split(' ')[0] || 'Developer'}</span>
          </h1>
          <p className="text-gray-400 text-sm">Here's a quick overview of your workspace today.</p>
        </div>
        
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${tierInfo.color}`}>
          <Zap size={16} />
          <span className="text-sm font-semibold">{tierInfo.name}</span>
        </div>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: Quick Actions (Span 3) */}
        <div className="lg:col-span-3 space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
          
          <button 
            onClick={() => navigate('/community')}
            className="w-full flex items-center justify-between p-4 bg-surface-container-low border border-border-muted hover:border-dev-blue/50 hover:bg-surface-container rounded-xl transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-dev-blue/10 text-dev-blue rounded-lg group-hover:scale-110 transition-transform">
                <Users size={18} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-200">Community Dev Board</p>
                <p className="text-xs text-gray-500">Get support and discuss</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-gray-600 group-hover:text-dev-blue group-hover:translate-x-1 transition-all" />
          </button>

          {userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org' ? (
            <button 
              onClick={() => navigate('/review')}
              className="w-full flex items-center justify-between p-4 bg-surface-container-low border border-border-muted hover:border-ai-purple/50 hover:bg-surface-container rounded-xl transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-ai-purple/10 text-ai-purple rounded-lg group-hover:scale-110 transition-transform">
                  <Terminal size={18} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-200">AI Code Review</p>
                  <p className="text-xs text-gray-500">Analyze code with Gemini</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-gray-600 group-hover:text-ai-purple group-hover:translate-x-1 transition-all" />
            </button>
          ) : (
            <div 
              className="w-full flex items-center justify-between p-4 bg-surface-container-low border border-border-muted rounded-xl opacity-80 cursor-not-allowed"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-800 text-gray-500 rounded-lg">
                  <Terminal size={18} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-400 flex items-center gap-2">
                    AI Code Review
                    <span className="text-[10px] uppercase font-bold bg-amber-500/20 text-amber-500 px-1.5 py-0.5 rounded border border-amber-500/30">Premium</span>
                  </p>
                  <p className="text-xs text-gray-600">Analyze code with Gemini</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-gray-700" />
            </div>
          )}

          {currentUser && (
            <button 
              onClick={() => navigate('/settings')}
              className="w-full flex items-center justify-between p-4 bg-surface-container-low border border-border-muted hover:border-gray-600 hover:bg-surface-container rounded-xl transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-800 text-gray-300 rounded-lg group-hover:scale-110 transition-transform">
                  <Settings size={18} />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-200">Account Settings</p>
                  <p className="text-xs text-gray-500">Manage profile and billing</p>
                </div>
              </div>
              <ArrowRight size={16} className="text-gray-600 group-hover:text-gray-300 group-hover:translate-x-1 transition-all" />
            </button>
          )}

          <a 
            href="/downloads"
            className="w-full flex items-center justify-between p-4 bg-surface-container-low border border-border-muted hover:border-cyan-500/50 hover:bg-surface-container rounded-xl transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/10 text-cyan-400 rounded-lg group-hover:scale-110 transition-transform">
                <Download size={18} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-200">Download Desktop App</p>
                <p className="text-xs text-gray-500">SnipIT native for Mac/Windows</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-gray-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
          </a>

          <a 
            href="https://docs.snipit.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-between p-4 bg-surface-container-low border border-border-muted hover:border-links-emerald/50 hover:bg-surface-container rounded-xl transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-links-emerald/10 text-links-emerald rounded-lg group-hover:scale-110 transition-transform">
                <BookOpen size={18} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-200">View Documentation</p>
                <p className="text-xs text-gray-500">Learn how to use SnipIT</p>
              </div>
            </div>
            <ArrowRight size={16} className="text-gray-600 group-hover:text-links-emerald group-hover:translate-x-1 transition-all" />
          </a>
        </div>

        {/* Center Column: Info Banner Carousel & Teams (Span 6) */}
        <div className="lg:col-span-6 space-y-8">
          {/* Info Banner / Feature Highlight (Carousel) */}
          <div className="w-full bg-linear-to-br from-indigo-900/40 via-surface-container-low to-bg-main border border-indigo-500/20 rounded-2xl flex flex-col relative overflow-hidden group min-h-100">
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
            
            {/* Slide Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              <button onClick={() => setCurrentSlide(0)} className={`w-2 h-2 rounded-full transition-all ${currentSlide === 0 ? 'bg-indigo-500 w-4' : 'bg-gray-600 hover:bg-gray-500'}`} aria-label="Slide 1" />
              <button onClick={() => setCurrentSlide(1)} className={`w-2 h-2 rounded-full transition-all ${currentSlide === 1 ? 'bg-indigo-500 w-4' : 'bg-gray-600 hover:bg-gray-500'}`} aria-label="Slide 2" />
              <button onClick={() => setCurrentSlide(2)} className={`w-2 h-2 rounded-full transition-all ${currentSlide === 2 ? 'bg-indigo-500 w-4' : 'bg-gray-600 hover:bg-gray-500'}`} aria-label="Slide 3" />
            </div>

            <div 
              className="relative z-10 flex w-[300%] h-full transition-transform duration-700 ease-in-out" 
              style={{ transform: `translateX(-${currentSlide * 33.333333}%)` }}
            >
              
              {/* Slide 1: Tier Info */}
              <div className="w-1/3 h-full flex flex-col justify-center p-8">
                {userLicense?.tier === 'enterprise_org' ? (
                  <>
                    <h3 className="text-2xl font-bold text-white mb-3">Enterprise Collaboration</h3>
                    <p className="text-gray-400 mb-6 max-w-lg leading-relaxed">
                      Manage your organization's teams, configure advanced security rules, and oversee all shared vaults from your admin dashboard.
                    </p>
                    <div>
                      <button 
                        onClick={() => navigate('/org-admin')}
                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-lg shadow-lg shadow-emerald-900/30 transition-all inline-flex items-center gap-2"
                      >
                        <Building size={16} /> Manage Organization
                      </button>
                    </div>
                  </>
                ) : userLicense?.tier === 'enterprise' || userLicense?.tier === 'premium' ? (
                  <>
                    <h3 className="text-2xl font-bold text-white mb-3">Maximize Your Workflow</h3>
                    <p className="text-gray-400 mb-6 max-w-lg leading-relaxed">
                      Snip-IT keeps your team in the flow state. Use our offline developer tools to decode JWTs, test regular expressions, and convert colors without ever leaving the secure environment.
                    </p>
                    <div>
                      <button 
                        onClick={() => setVaultAndNavigate('tools')}
                        className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-lg shadow-lg shadow-indigo-900/30 transition-all inline-flex items-center gap-2"
                      >
                        <LayoutDashboard size={16} /> Explore Tools
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-white mb-3">Unlock AI Power</h3>
                    <p className="text-gray-400 mb-6 max-w-lg leading-relaxed">
                      Upgrade your workspace to access advanced AI code reviews with Gemini, unlimited cloud storage for your snippets, and team collaboration features.
                    </p>
                    <div>
                      <button 
                        onClick={() => navigate('/billing')}
                        className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-lg shadow-lg shadow-amber-900/30 transition-all inline-flex items-center gap-2"
                      >
                        <Zap size={16} /> Upgrade to Pro
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Slide 2: DevTime Promo */}
              <div className="w-1/3 h-full flex flex-col md:flex-row items-center justify-between p-8 gap-6 bg-blue-900/10">
                <div className="flex-1 text-left">
                  <p className="text-blue-400 text-xs font-bold tracking-[0.2em] uppercase mb-2">{t('More from DevIT')}</p>
                  <h3 className="text-2xl font-bold text-white mb-3">{t('Check out DevTime')}</h3>
                  <p className="text-gray-400 mb-6 max-w-lg leading-relaxed">
                    {t('Time tracking straight from your browser history. Integrate your development flow with automated tracking and seamless insights.')}
                  </p>
                  <a 
                    href="https://chromewebstore.google.com/detail/devtime/laacdocceefbggihaekapebfjicobpbk" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg shadow-lg shadow-blue-900/30 transition-all inline-flex items-center gap-2"
                  >
                    {t('View on Chrome Web Store')}
                  </a>
                </div>
                <div className="flex-none hidden md:block pr-8">
                  <img src="/assets/devtime-promo.png" alt="DevTime Promo" className="h-32 rounded-xl shadow-2xl border border-gray-700/50 object-contain rotate-2 hover:rotate-0 transition-transform duration-300" />
                </div>
              </div>

              {/* Slide 3: Desktop App Promo */}
              <div className="w-1/3 h-full flex flex-col md:flex-row items-center justify-between p-8 gap-6 bg-purple-900/10">
                <div className="flex-1 text-left">
                  <p className="text-purple-400 text-xs font-bold tracking-[0.2em] uppercase mb-2">{t('NATIVE EXPERIENCE')}</p>
                  <h3 className="text-2xl font-bold text-white mb-3">{t('Download SnipIT Desktop')}</h3>
                  <p className="text-gray-400 mb-6 max-w-lg leading-relaxed">
                    {t('Experience lightning-fast local performance, global keyboard shortcuts, and deep OS integration with our native Desktop Application.')}
                  </p>
                  <button 
                    onClick={() => navigate('/downloads')}
                    className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold rounded-lg shadow-lg shadow-purple-900/30 transition-all inline-flex items-center gap-2"
                  >
                    {t('Download Now')}
                  </button>
                </div>
                <div className="flex-none hidden md:block pr-8 text-purple-400/20">
                  <Terminal size={120} strokeWidth={1} />
                </div>
              </div>

            </div>
          </div>
          
          {/* My Team Section */}
          {userLicense?.tier === 'enterprise_org' && teams && teams.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Organization Teams</h2>
                <button onClick={() => navigate('/teams')} className="text-sm font-semibold text-links-emerald hover:text-emerald-300 transition-colors">
                  Manage Teams &rarr;
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {teams.slice(0, 4).map((team) => (
                  <div key={team.id} className="bg-surface-container-low border border-border-muted p-4 rounded-xl flex flex-col gap-3 hover:border-links-emerald/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-links-emerald/20 text-links-emerald flex items-center justify-center font-bold text-lg">
                        {team.name?.charAt(0) || 'T'}
                      </div>
                      <div>
                        <h4 className="text-white font-bold">{team.name}</h4>
                        <p className="text-gray-500 text-xs">{team.memberIds?.length || 0} Members</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Workspace Stats (Span 3) */}
        <div className="lg:col-span-3 space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">Workspace Overview</h2>
          <div className="flex flex-col gap-4">
            <div className="p-4 bg-surface-container-low border border-border-muted rounded-xl flex items-center justify-between hover:border-dev-blue/30 transition-all cursor-pointer group" onClick={() => setVaultAndNavigate('dev')}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-dev-blue/10 text-dev-blue rounded-lg group-hover:bg-dev-blue/20 transition-colors"><Code size={18} /></div>
                <span className="font-semibold text-gray-200">Dev Snippets</span>
              </div>
              <span className="text-xl font-bold text-white">{snippetStats.dev}</span>
            </div>
            
            <div className="p-4 bg-surface-container-low border border-border-muted rounded-xl flex items-center justify-between hover:border-ai-purple/30 transition-all cursor-pointer group" onClick={() => setVaultAndNavigate('ai')}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-ai-purple/10 text-ai-purple rounded-lg group-hover:bg-ai-purple/20 transition-colors"><Terminal size={18} /></div>
                <span className="font-semibold text-gray-200">AI Prompts</span>
              </div>
              <span className="text-xl font-bold text-white">{snippetStats.ai}</span>
            </div>

            <div className="p-4 bg-surface-container-low border border-border-muted rounded-xl flex items-center justify-between hover:border-links-emerald/30 transition-all cursor-pointer group" onClick={() => setVaultAndNavigate('links')}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-links-emerald/10 text-links-emerald rounded-lg group-hover:bg-links-emerald/20 transition-colors"><Globe size={18} /></div>
                <span className="font-semibold text-gray-200">Saved Links</span>
              </div>
              <span className="text-xl font-bold text-white">{snippetStats.links}</span>
            </div>

            <div className="p-4 bg-surface-container-low border border-border-muted rounded-xl flex items-center justify-between hover:border-tools-amber/30 transition-all cursor-pointer group" onClick={() => setVaultAndNavigate('tools')}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-tools-amber/10 text-tools-amber rounded-lg group-hover:bg-tools-amber/20 transition-colors"><LayoutDashboard size={18} /></div>
                <span className="font-semibold text-gray-200">Tool Configs</span>
              </div>
              <span className="text-xl font-bold text-white">{snippetStats.tools}</span>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
}
